import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import { checkSubscription } from './subscriptionGuard'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      title: 'TutorMed - Medical Education Platform | MCQ & OSCE Training',
      description: 'Join thousands of healthcare professionals worldwide. Master MCQ practice, OSCE training, and clinical skills with TutorMed\'s comprehensive medical education platform.',
      keywords: 'medical education, MCQ practice, OSCE training, medical students, healthcare professionals'
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: {
      title: 'About TutorMed - Medical Education for Healthcare Professionals',
      description: 'Learn about TutorMed\'s mission to simplify complex medical concepts for healthcare professionals worldwide. Discover our story and commitment to medical education.',
      keywords: 'about tutormed, medical education mission, healthcare training, medical learning platform'
    }
  },
  {
    path: '/review/:token',
    name: 'PublicReview',
    component: () => import('../views/PublicReviewPage.vue'),
    meta: {
      title: 'Share Your Review - TutorMed',
      description: 'Share your experience with TutorMed. Your feedback helps us improve our services.'
    }
  },
  {
    path: '/programs',
    name: 'Programs',
    component: () => import('../views/Programs.vue'),
    meta: {
      title: 'Medical Education Programs | TutorMed Training Courses',
      description: 'Explore specialized medical education programs for students, doctors, nurses, and physician assistants. MCQ practice, OSCE training, and comprehensive course modules.',
      keywords: 'medical programs, healthcare courses, medical student training, doctor education, nursing courses'
    }
  },
  {
    path: '/programs/:id',
    name: 'ProgramDetail',
    component: () => import('../views/ProgramDetail.vue'),
    props: true
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue')
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue')
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue')
  },
  {
    path: '/reset-password/:uidb64/:token',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue'),
    props: true
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('../views/AdminLogin.vue')
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('../views/policies/PrivacyPolicy.vue')
  },
  {
    path: '/app-privacy',
    name: 'AppPrivacyPolicy',
    component: () => import('../views/policies/AppPrivacyPolicy.vue'),
    meta: { title: 'TutorMedics App Privacy Policy | TutorMed' }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: () => import('../views/policies/TermsOfService.vue')
  },
  {
    path: '/cookie-policy',
    name: 'CookiePolicy',
    component: () => import('../views/policies/CookiePolicy.vue')
  },
  {
    path: '/debug-exams',
    name: 'DebugExams',
    component: () => import('../views/DebugExams.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/free-resources',
    name: 'FreeResources',
    component: () => import('../views/FreeResources.vue')
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/Contact.vue'),
    meta: {
      title: 'Contact TutorMed - Get Medical Education Support',
      description: 'Contact TutorMed for medical education support, technical assistance, or partnership inquiries. We\'re here to help healthcare professionals succeed.',
      keywords: 'contact tutormed, medical education support, customer service, healthcare training help'
    }
  },
  {
    path: '/help/getting-started',
    name: 'GettingStarted',
    component: () => import('../views/help/GettingStarted.vue'),
    meta: {
      title: 'Getting Started with TutorMed - Medical Education Guide',
      description: 'Learn how to get started with TutorMed\'s medical education platform. Step-by-step guide for healthcare professionals to maximize their learning experience.',
      keywords: 'tutormed getting started, medical education guide, how to use tutormed, healthcare learning platform'
    }
  },
  {
    path: '/help/faq',
    name: 'FAQ',
    component: () => import('../views/help/FAQ.vue'),
    meta: {
      title: 'Frequently Asked Questions - TutorMed Help Center',
      description: 'Find answers to common questions about TutorMed\'s medical education platform, subscriptions, courses, and technical support.',
      keywords: 'tutormed faq, medical education questions, help center, tutormed support'
    }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('../views/Pricing.vue'),
    meta: { requiresAuth: true }  // Requires auth but not subscription
  },
  {
    // Students on an "App only" plan: website study pages point them to the TutorMedics app
    path: '/app-only',
    name: 'AppOnly',
    component: () => import('../views/AppOnly.vue'),
    meta: { requiresAuth: true }
  },
  // Add these routes inside the routes array
{
    path: '/courses/osce',
    name: 'OsceCourse',
    component: () => import('../views/courses/OsceCourse.vue'),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/courses/mcq',
    name: 'McqCourse',
    component: () => import('../views/courses/McqCourse.vue'),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/mock-exams/osce/:id',
    name: 'OsceExam',
    component: () => import('../views/exams/UnmannedOsceExam.vue'),
    props: route => ({
      examId: route.params.id
    }),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/mock-exams/mcq/:id',
    name: 'McqExam',
    component: () => import('../views/exams/MockExam.vue'),
    props: route => ({
      examId: route.params.id,
      examType: 'MCQ'
    }),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/exams/mock-exam',
    name: 'MockExam',
    component: () => import('../views/exams/ExamRouter.vue'),
    props: route => ({
      examId: route.query.id,
      examType: route.query.type?.toString().toUpperCase() || ''
    }),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/exams/diagnostic-exam/:id',
    name: 'DiagnosticExam',
    component: () => import('../views/exams/DiagnosticExam.vue'),
    props: route => ({
      examId: route.params.id,
      examType: 'DIAGNOSTIC'
    }),
    meta: { requiresAuth: true }
  },
  {
    path: '/exams/candidate-drill/:id',
    name: 'CandidateDrillExam',
    component: () => import('../views/exams/CandidateDrillExam.vue'),
    props: route => ({
      examId: route.params.id,
      examType: 'CANDIDATE_DRILL'
    }),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/exams/manned-osce',
    name: 'MannedOsceExam',
    component: () => import('../views/exams/MannedOsceExam.vue'),
    props: route => ({
      stationId: route.query.stationId,
      examId: route.query.examId
    }),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/courses/basic-sciences',
    name: 'BasicSciences',
    component: () => import('../views/courses/BasicSciences.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/courses/clinical-sciences',
    name: 'ClinicalSciences',
    component: () => import('../views/courses/ClinicalSciences.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/courses/osce-preparation',
    name: 'OscePreparation',
    component: () => import('../views/courses/OsceCourse.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/courses/mcq-practice',
    name: 'McqPractice',
    component: () => import('../views/courses/McqCourse.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/courses/nursing-care',
    name: 'NursingCare',
    component: () => import('../views/courses/BasicSciences.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/courses/pa-skills',
    name: 'PASkills',
    component: () => import('../views/courses/BasicSciences.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true, requiresSubscription: true },
    props: route => ({ initialSection: route.query.section })
  },
  {
    path: '/courses',
    name: 'AllCourses',
    component: () => import('../views/courses/AllCourses.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/courses/:id',
    name: 'CourseDetails',
    component: () => import('../views/CourseDetail.vue'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('../views/Notifications.vue'),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/messages',
    name: 'Messages',
    component: () => import('../views/Messages.vue'),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('../views/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    props: route => ({ initialSection: route.query.section })
  },
  {
    path: '/admin/courses/create',
    name: 'CreateCourse',
    component: () => import('../views/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    props: { initialSection: 'courses', createMode: true }
  },
  {
    path: '/admin/mcq-exams/:id/questions',
    name: 'ManageExamQuestions',
    component: () => import('../views/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    props: route => ({ 
      initialSection: 'questions',
      examId: route.params.id
    })
  },
  {
    path: '/admin/videos/upload',
    name: 'UploadVideo',
    component: () => import('../views/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    props: { initialSection: 'videos', uploadMode: true }
  },
  {
    path: '/admin/courses/:id',
    name: 'AdminCourseDetail',
    component: () => import('../views/CourseDetail.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    props: true
  },
  {
    path: '/button-demo',
    name: 'ButtonDemo',
    component: () => import('../views/ButtonDemo.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/cardiovascular-station/:stationId',
    name: 'CardiovascularStation',
    component: () => import('../views/CardiovascularStation.vue'),
    props: true,
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/respiratory-station/:stationId',
    name: 'RespiratoryStation',
    component: () => import('../views/RespiratoryStation.vue'),
    props: true,
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/gastrointestinal-station/:stationId',
    name: 'GastrointestinalStation',
    component: () => import('../views/GastrointestinalStation.vue'),
    props: true,
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/neurological-station/:stationId',
    name: 'NeurologicalStation',
    component: () => import('../views/NeurologicalStation.vue'),
    props: true,
    meta: { requiresAuth: true, requiresSubscription: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Update page meta tags for SEO
router.afterEach((to) => {
  // Update page title
  if (to.meta.title) {
    document.title = to.meta.title;
  }
  
  // Update meta description
  if (to.meta.description) {
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', to.meta.description);
    }
  }
  
  // Update meta keywords
  if (to.meta.keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', to.meta.keywords);
    }
  }
  
  // Update Open Graph meta tags
  if (to.meta.title) {
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', to.meta.title);
    }
  }
  
  if (to.meta.description) {
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', to.meta.description);
    }
  }
  
  // Update Twitter meta tags
  if (to.meta.title) {
    let twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', to.meta.title);
    }
  }
  
  if (to.meta.description) {
    let twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', to.meta.description);
    }
  }
});

// Navigation guard for authentication and admin role
router.beforeEach((to, from, next) => {
  // "App only" plans study in the TutorMedics app: the website's study pages show the app page instead.
  try {
    const appUser = JSON.parse(localStorage.getItem('user') || '{}');
    const appRole = appUser.role && typeof appUser.role === 'object' ? appUser.role.name : appUser.role;
    const isAppOnly = appUser.study_platform === 'mobile' && appRole !== 'admin' && !appUser.is_staff && !appUser.is_superuser;
    if (isAppOnly && /^\/(dashboard|courses|exams)(\/|$)/.test(to.path)) {
      next('/app-only');
      return;
    }
  } catch (e) {
    // Ignore unreadable user data; the normal checks below run.
  }

  // Only log in development, not in production
  const isDev = process.env.NODE_ENV !== 'production';
  
  if (isDev) {
    console.log(`Router: Navigating from ${from.path} to ${to.path}`);
  }
  
  // Get authentication status and user data from localStorage
  const token = localStorage.getItem('authToken');
  let isAuthenticated = !!token;  // Changed to 'let' since we might need to update it
  
  if (isDev) {
    console.log('Router: Auth check -', 
      'token exists:', !!token,
      'token:', token ? token.substring(0, 10) + '...' : 'none'
    );
  }
  
  // Parse user data with error handling
  let userData = null;
  let userRole = null;
  let isAdmin = false;
  let isTutor = false;
  
  try {
    // Get user data from localStorage
    const userDataString = localStorage.getItem('user');
    
    if (userDataString) {
      try {
        userData = JSON.parse(userDataString);
        
        // Extract role information from various possible formats
        userRole = userData?.role_details?.name || 
                  (userData?.role && typeof userData.role === 'object' ? userData.role.name : userData?.role);
        
        isAdmin = userRole === 'admin';
        isTutor = userRole === 'tutor';
        
        if (isDev) {
          console.log('Router: User role -', 
            'userRole:', userRole, 
            'isAdmin:', isAdmin, 
            'isTutor:', isTutor
          );
        }
      } catch (parseError) {
        console.error('Router: Error parsing user data:', parseError);
        // Reset authentication if user data is corrupted
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        isAuthenticated = false;
      }
    }
  } catch (e) {
    console.error('Router: Error accessing localStorage:', e);
  }
  
  // Public routes that don't require authentication
  const publicRoutes = ['Home', 'About', 'Programs', 'ProgramDetail', 'FreeResources',
                        'Login', 'Register', 'AdminLogin', 'ForgotPassword', 'ResetPassword',
                        'PrivacyPolicy', 'TermsOfService', 'CookiePolicy', 'ButtonDemo',
                        'Contact', 'GettingStarted', 'FAQ', 'PublicReview'];
                        
  if (publicRoutes.includes(to.name)) {
    // Login/register pages handling - redirect to dashboard if already logged in
    if ((to.path === '/login' || to.path === '/register') && isAuthenticated) {
      console.log('Router: User already logged in, redirecting from auth page to dashboard');
      next('/dashboard');
      return;
    }
    
    // Admin login special case
    if (to.path === '/admin/login') {
      if (isAuthenticated && isAdmin) {
        console.log('Router: Admin already logged in, redirecting to admin dashboard');
        next('/admin');
        return;
      }
    }
    
    console.log('Router: Allowing access to public page:', to.path);
    next();
    return;
  }
  
  // For all other routes, check authentication
  if (!isAuthenticated) {
    // Not authenticated - redirect to appropriate login page
    if (isDev) {
      console.log('Router: Not authenticated, redirecting to login');
    }
    
    // Store the intended destination for redirection after login (but not for login pages)
    if (to.name !== 'Login' && to.name !== 'Register' && to.name !== 'AdminLogin') {
      const redirect = to.fullPath;
      localStorage.setItem('redirectAfterLogin', redirect);
      
      if (isDev) {
        console.log('Router: Saving redirect path:', redirect);
      }
    }
    
    // Redirect to appropriate login page
    if (to.matched.some(record => record.meta.requiresAdmin)) {
      next('/admin/login');
    } else {
      next('/login');
    }
    return;
  }
  
  // User is authenticated - check roles for restricted pages
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    if (!isAdmin) {
      if (isDev) {
        console.log('Router: User is not admin, redirecting to dashboard');
      }
      
      // Show error notification if available
      if (window.$toast) {
        window.$toast.error('You do not have permission to access this page.');
      }
      
      next('/dashboard');
      return;
    } else if (isDev) {
      console.log('Router: User is admin, allowing access to admin page');
    }
  } else if (to.matched.some(record => record.meta.requiresTutor) && !(isAdmin || isTutor)) {
    if (isDev) {
      console.log('Router: User is not tutor or admin, redirecting to dashboard');
    }
    
    // Show error notification if available
    if (window.$toast) {
      window.$toast.error('You do not have permission to access this page.');
    }
    
    next('/dashboard');
    return;
  }
  
  // All checks passed, allow navigation
  if (isDev) {
    console.log('Router: All checks passed, allowing navigation to', to.path);
  }
  
  // Check subscription status before allowing access to protected routes
  checkSubscription(to, from, next);
})

export default router